import FaqSchema from "@/components/seo/FaqSchema";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { useFaqs, localizeFaq } from "@/hooks/useFaqs";
import { useLanguage } from "@/hooks/useLanguage";
import { uiT } from "@/i18n/ui";

interface Props {
  slug: string;
  pageType?: string;
}

const BlogFAQ = ({ slug, pageType = "blog" }: Props) => {
  const lang = useLanguage();
  const { data: rawFaqs = [] } = useFaqs(pageType, slug);
  const faqs = rawFaqs.map((f) => localizeFaq(f as any, lang));

  if (!faqs.length) return null;

  return (
    <section
      className="mt-16 pt-10 border-t border-border/50"
      itemScope
      itemType="https://schema.org/FAQPage"
      aria-labelledby="blog-faq-heading"
    >
      <FaqSchema items={faqs} />

      <h2
        id="blog-faq-heading"
        className="text-2xl sm:text-3xl font-bold text-foreground mb-6"
      >
        {uiT(lang)("faqTitle")}
      </h2>

      <Accordion type="single" collapsible className="w-full">
        {faqs.map((faq, i) => (
          <AccordionItem
            key={faq.id ?? i}
            value={`faq-${i}`}
            className="border-border/50"
            itemScope
            itemProp="mainEntity"
            itemType="https://schema.org/Question"
          >
            <AccordionTrigger className="text-left text-base font-medium text-foreground hover:text-primary [&[data-state=open]]:text-primary py-4">
              <span itemProp="name">{faq.question}</span>
            </AccordionTrigger>
            <AccordionContent
              className="text-sm text-muted-foreground leading-relaxed pb-4"
              itemScope
              itemProp="acceptedAnswer"
              itemType="https://schema.org/Answer"
            >
              <div itemProp="text">{faq.answer}</div>
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion>
    </section>
  );
};

export default BlogFAQ;
